/**
 * /admin/orders — full-database orders page (iter114s.76).
 *
 * Lifts the legacy "Order Management" tab into a dedicated route.
 * Renders the existing OrdersTable + OrderManagementModal pair with
 * the same data-fetching pattern the legacy AdminDashboard used.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from './AdminLayout';
import { OrdersTable } from './OrdersTable';
import { OrderManagementModal } from './OrderManagementModal';
import { InvoiceAgingChip } from './InvoiceAgingChip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderStatus, setOrderStatus] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/enhanced-orders/`, { withCredentials: true });
      setOrders(res.data || []);
    } catch {
      toast.error('Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openOrderModal = (o) => {
    setSelectedOrder(o);
    setTrackingNumber(o.tracking_number || '');
    setOrderStatus(o.status || '');
    setProofFile(null);
    setShowOrderModal(true);
  };
  const closeOrderModal = () => { setShowOrderModal(false); setSelectedOrder(null); };

  const handleUploadProof = async () => {
    if (!proofFile || !selectedOrder) return;
    const fd = new FormData();
    fd.append('order_id', selectedOrder.id);
    fd.append('file', proofFile);
    try {
      await axios.post(`${API_URL}/api/files/upload-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      toast.success('Proof uploaded.');
      fetchOrders();
      closeOrderModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Proof upload failed.');
    }
  };

  const handleUpdateOrderStatus = async () => {
    if (!selectedOrder) return;
    try {
      await axios.put(`${API_URL}/api/enhanced-orders/${selectedOrder.id}/status`, {
        status: orderStatus,
        tracking_number: trackingNumber || null,
      }, { withCredentials: true });
      toast.success('Order updated.');
      fetchOrders();
      closeOrderModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed.');
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-screen-2xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#14281D]" data-testid="admin-orders-heading">Orders</h1>
          <p className="text-sm text-[#1F3D2C]/70">Full order database — historical lookup, customer support, repeat orders, exports.</p>
        </header>
        <InvoiceAgingChip refreshKey={orders.length} />
        <OrdersTable orders={orders} loading={loading} onManageOrder={openOrderModal} />
      </div>
      <OrderManagementModal
        open={showOrderModal}
        order={selectedOrder}
        proofFile={proofFile}
        trackingNumber={trackingNumber}
        orderStatus={orderStatus}
        onClose={closeOrderModal}
        onProofFileChange={setProofFile}
        onUploadProof={handleUploadProof}
        onTrackingChange={setTrackingNumber}
        onStatusChange={setOrderStatus}
        onUpdateStatus={handleUpdateOrderStatus}
      />
    </AdminLayout>
  );
};

export default AdminOrders;
